import React, { useEffect, useState } from "react";
import { GoLocation } from "react-icons/go";
import { PiBagSimple } from "react-icons/pi";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  CandidateStatus,
  GetAllApplicants,
  GetOneJob,
} from "../../Redux/Slices/JobPost";
import { FaArrowLeft, FaIndustry, FaRupeeSign, FaUsers } from "react-icons/fa";
import { FcGraduationCap } from "react-icons/fc";
import { formatTimeAgo } from "../../Helper/FromatTime";
import { formatJobLocation } from "../../Helper/CoreHelper";
import {
  getApplicantCurrentRole,
  getApplicantExperienceDisplay,
} from "../../Helper/CoreHelper";
import AdminLayout from "./Common/AdminLayout";
import { TableRowSkeleton } from "./Common/LoadingSkeleton";
import "../../asset/new/css/admin-modern.css";

const Applicants = () => {
  const { id } = useParams();
  const role = localStorage.getItem("role");
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { jobdetails, jobapplicants } = useSelector((state) => state?.Jobs);
  const [loading, setLoading] = useState(true);
  const [entriesToShow, setEntriesToShow] = useState(10);
  const [searchQuery, setSearchQuery] = useState("");

  const applicants =
    jobapplicants?.filter((item) => item?.type === "applied") || [];

  const shortlistedUser =
    jobapplicants?.filter(
      (item) =>
        item?.type === "applied" && item?.action_status === "Shortlisted"
    ) || [];

  const filteredApplicants = applicants
    .filter(
      (item) =>
        item?.user_id?.name
          ?.toLowerCase()
          .includes(searchQuery.toLowerCase()) ||
        item?.user_id?.email?.toLowerCase().includes(searchQuery.toLowerCase())
    )
    .slice(0, entriesToShow);

  const backPath =
    role === "Employer" ? "/employer/posted-jobs" : "/admin/postedJobs";

  const profilePath = (userId, jobId) =>
    role === "Employer"
      ? `/employer/applicant-profile/${userId}/jobid/${jobId}`
      : `/admin/applicant-profile/${userId}/jobid/${jobId}`;

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      await Promise.all([
        dispatch(GetOneJob(id)),
        dispatch(GetAllApplicants(id)),
      ]);
      setLoading(false);
    };
    fetchData();
    document.title = "Job Applicants | CAJobPortal";
  }, [id, dispatch]);

  const handleSeen = async (userId, jobId, currentAction) => {
    if (
      role === "Employer" &&
      !["Viewed", "Downloaded", "Shortlisted", "Not Suitable"].includes(
        currentAction
      )
    ) {
      await CandidateStatus({
        user_id: userId,
        job_id: jobId,
        action: "Viewed",
      });
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case "Shortlisted":
        return "admin-badge-success";
      case "Not Suitable":
        return "admin-badge-danger";
      case "Downloaded":
        return "admin-badge-info";
      case "Viewed":
        return "admin-badge-warning";
      default:
        return "admin-badge-secondary";
    }
  };

  return (
    <AdminLayout
      title="Job Applicants"
      subtitle={`Manage applicants for ${jobdetails?.title || "this position"}`}
    >
      <button
        type="button"
        className="admin-applicant-back"
        onClick={() => navigate(backPath)}
      >
        <FaArrowLeft size={12} /> Back to Posted Jobs
      </button>

      {loading ? (
        <TableRowSkeleton rows={5} />
      ) : (
        <>
          <div className="admin-card admin-job-summary-card">
            <div className="admin-card-body">
              <h2 className="admin-job-summary-title">{jobdetails?.title}</h2>
              <p className="admin-job-summary-company">{jobdetails?.company_name}</p>
              <div className="admin-job-summary-tags">
                {jobdetails?.exp?.from !== undefined &&
                  jobdetails?.exp?.to !== undefined && (
                    <span className="admin-job-summary-tag">
                      <PiBagSimple size={12} />
                      {jobdetails.exp.from}-{jobdetails.exp.to} Years
                    </span>
                  )}
                {jobdetails?.ctc?.from_lac !== undefined && (
                  <span className="admin-job-summary-tag">
                    <FaRupeeSign size={11} />
                    {jobdetails.ctc.from_lac}.{jobdetails.ctc.from_thousand || 0} -{" "}
                    {jobdetails.ctc.to_lac}.{jobdetails.ctc.to_thousand || 0} Lacs
                  </span>
                )}
                {formatJobLocation(jobdetails) && (
                  <span className="admin-job-summary-tag">
                    <GoLocation size={12} />
                    {formatJobLocation(jobdetails)}
                  </span>
                )}
                {jobdetails?.industry?.name && (
                  <span className="admin-job-summary-tag">
                    <FaIndustry size={11} />
                    {jobdetails.industry.name}
                  </span>
                )}
                <span className="admin-job-summary-tag">
                  <FcGraduationCap size={12} />
                  {jobdetails?.category || "Job"}
                </span>
              </div>
            </div>
          </div>

          <div className="admin-applicant-stats">
            <div className="admin-applicant-stat">
              <div className="admin-applicant-stat-label">Applicants</div>
              <div className="admin-applicant-stat-value">
                {jobdetails?.applications?.length || applicants.length || 0}
              </div>
            </div>
            <div className="admin-applicant-stat">
              <div className="admin-applicant-stat-label">Views</div>
              <div className="admin-applicant-stat-value">
                {jobdetails?.views || 0}
              </div>
            </div>
            <div className="admin-applicant-stat">
              <div className="admin-applicant-stat-label">Shortlisted</div>
              <div className="admin-applicant-stat-value">
                {shortlistedUser.length}
              </div>
            </div>
          </div>

          <div className="admin-table-wrapper">
            <div className="admin-table-header">
              <h3 className="admin-table-title">
                <FaUsers size={14} style={{ marginRight: "0.375rem" }} />
                Applicant List
              </h3>
              <div className="admin-table-actions">
                <select
                  className="admin-select"
                  style={{ width: "auto", minWidth: "90px", padding: "0.5rem 2rem 0.5rem 0.75rem", fontSize: "0.8125rem" }}
                  value={entriesToShow}
                  onChange={(e) => setEntriesToShow(Number(e.target.value))}
                >
                  <option value={10}>10 rows</option>
                  <option value={25}>25 rows</option>
                  <option value={50}>50 rows</option>
                  <option value={100}>100 rows</option>
                </select>
                <div className="admin-table-search">
                  <input
                    type="search"
                    placeholder="Search applicants..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    style={{ fontSize: "0.8125rem", padding: "0.5rem 0.75rem 0.5rem 2rem" }}
                  />
                </div>
              </div>
            </div>

            <div className="admin-table-container">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Applicant Name</th>
                    <th>Applied On</th>
                    <th>Experience</th>
                    <th>Action</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredApplicants.length > 0 ? (
                    filteredApplicants.map((item, index) => (
                      <tr key={item?._id || index}>
                        <td>{index + 1}</td>
                        <td>
                          <div style={{ fontSize: "0.8125rem", fontWeight: 500 }}>
                            {item?.user_id?.name}
                          </div>
                          <div style={{ fontSize: "0.75rem", color: "var(--admin-text-muted)" }}>
                            {item?.user_id?.email}
                          </div>
                        </td>
                        <td style={{ fontSize: "0.8125rem" }}>
                          {formatTimeAgo(new Date(item?.created_at))}
                        </td>
                        <td>
                          <div style={{ fontSize: "0.8125rem", fontWeight: 500 }}>
                            {getApplicantExperienceDisplay(item?.user_id)}
                          </div>
                          {getApplicantCurrentRole(item?.user_id) && (
                            <div
                              style={{
                                fontSize: "0.75rem",
                                color: "var(--admin-text-muted)",
                              }}
                            >
                              {getApplicantCurrentRole(item?.user_id)}
                            </div>
                          )}
                        </td>
                        <td>
                          <Link
                            className="admin-btn admin-btn-sm admin-btn-ghost"
                            style={{ fontSize: "0.75rem", textDecoration: "none" }}
                            onClick={() =>
                              handleSeen(
                                item?.user_id?._id,
                                item?.job_id,
                                item?.action_status
                              )
                            }
                            to={profilePath(item?.user_id?._id, item?.job_id)}
                          >
                            View Profile
                          </Link>
                        </td>
                        <td>
                          <span
                            className={`admin-badge ${getStatusBadgeClass(
                              item?.action_status
                            )}`}
                            style={{ fontSize: "0.6875rem" }}
                          >
                            {item?.action_status || "Pending"}
                          </span>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="6">
                        <div className="admin-empty-state" style={{ padding: "2rem 1rem" }}>
                          <FaUsers size={28} style={{ opacity: 0.4, marginBottom: "0.5rem" }} />
                          <h4 style={{ fontSize: "0.9375rem", marginBottom: "0.25rem" }}>
                            No Applicants
                          </h4>
                          <p style={{ fontSize: "0.8125rem", margin: 0 }}>
                            No applicants have applied for this job yet.
                          </p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="admin-table-footer">
              <span className="admin-table-info" style={{ fontSize: "0.75rem" }}>
                Showing {filteredApplicants.length} of {applicants.length} applicants
              </span>
            </div>
          </div>
        </>
      )}
    </AdminLayout>
  );
};

export default Applicants;
